import { useEffect, useMemo, useRef, useState } from 'react';
import { PanelLeftClose, PanelLeftOpen, X } from 'lucide-react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { navigation, navigationGroups } from '@/pages/pages';
import { GridToolbarProvider } from '@/components/gridToolbar';
import { canOpen, landingKey } from '@/lib/navAccess';
import { useAuth } from '@/auth/AuthProvider';
import { RequireAuth } from '@/auth/RequireAuth';
import { isSupabaseConfigured } from '@/lib/supabase';
import { ToastHost } from '@/components/live';
import { OrgAwayBanner } from '@/components/OrgSwitcher';
import { ChangeReasonHost, OrgChooserOnLogin } from '@/components/HeaderControls';
import { VanamLogo } from '@/components/VanamLogo';
import { SideNav } from '@/components/SideNav';
import { BottomNav } from '@/components/BottomNav';
import { MobileHeader } from '@/components/MobileHeader';

/**
 * The signed-in shell. Navigation entries are filtered by the caller's permissions, and each
 * route is wrapped in the same check, so typing a URL is no way around a missing grant.
 */
export default function AppShell() {
  const { status, profile } = useAuth();
  const location = useLocation();
  const [search, setSearch] = useState('');
  // The menu is a drawer below tablet width; on a desktop it is always there and this is unused.
  const [menuOpen, setMenuOpen] = useState(false);
  // Hub's rail: the menu narrows to icons so a dense screen gets its width back. Remembered
  // per browser, and a storage failure just means it starts expanded.
  const [railed, setRailed] = useState(() => {
    try { return window.localStorage.getItem('vanam.nav.railed') === '1'; } catch { return false; }
  });
  // Hub's hover: while the pointer rests on the rail it widens over the page, and narrows again
  // when the pointer leaves. A short delay keeps a pointer merely crossing it from opening it.
  const [peek, setPeek] = useState(false);
  const peekTimer = useRef<number | undefined>(undefined);
  // On a phone or tablet the menu is a drawer, always full width: the rail is a desktop affordance.
  const [narrow, setNarrow] = useState(() => window.matchMedia('(max-width: 900px)').matches);
  useEffect(() => {
    const query = window.matchMedia('(max-width: 900px)');
    const onChange = () => setNarrow(query.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);
  const compact = railed && !peek && !narrow;
  function startPeek() {
    if (!railed || window.matchMedia('(max-width: 900px)').matches) return;
    window.clearTimeout(peekTimer.current);
    peekTimer.current = window.setTimeout(() => setPeek(true), 450);
  }
  function endPeek() {
    window.clearTimeout(peekTimer.current);
    setPeek(false);
  }
  useEffect(() => () => window.clearTimeout(peekTimer.current), []);

  // With no backend configured the prototype shell still runs, and shows everything.
  const offline = status === 'offline';
  const visibleGroups = useMemo(
    () => navigationGroups
      .map((group) => ({ ...group, items: group.items.filter((item) => offline || canOpen(profile, item.key)) }))
      .filter((group) => group.items.length > 0),
    [profile, offline],
  );
  const landing = useMemo(
    () => (offline ? 'dashboard' : landingKey(profile, navigation.map((item) => item.key))),
    [profile, offline],
  );
  const activeKey = location.pathname.replace(/^\//, '') || landing;

  useEffect(() => { setMenuOpen(false); setPeek(false); }, [location.pathname]);

  useEffect(() => {
    try { window.localStorage.setItem('vanam.nav.railed', railed ? '1' : '0'); } catch { /* optional */ }
  }, [railed]);

  return (
    // The grid on the page publishes its search, columns, export and selection to the header
    // through this (owner, 2026-09-25). One store, because one page shows one register.
    <GridToolbarProvider>
    <div className="app-shell">
      {menuOpen && <div className="sidebar-scrim" onClick={() => setMenuOpen(false)} />}
      <aside className={`sidebar${menuOpen ? ' open' : ''}${railed && !narrow ? ' railed' : ''}${peek ? ' peek' : ''}${compact ? ' compact' : ''}`}
             onMouseEnter={startPeek} onMouseLeave={endPeek}>
        <button className="sidebar-close" onClick={() => setMenuOpen(false)} type="button" aria-label="Close menu">
          <X size={18} />
        </button>
        <div className="brand">
          {/*
            * Bigger (owner, 2026-09-25: "increase the logo size on left menu?"). The brand area
            * is 243px wide and the artwork is 2.36:1, so 88 renders about 207 wide and still
            * clears the edges; `max-width: 100%` in the stylesheet is what actually stops it
            * overflowing if the sidebar is ever narrowed.
            */}
          <VanamLogo height={compact ? 34 : 88} title="Vanam" variant={compact ? 'mark' : 'compact'} />
          {!compact && <span>FSC and EUDR Project Management</span>}
          <button
            aria-label={railed ? 'Keep the menu open' : 'Collapse the menu to icons'}
            className="rail-toggle"
            onClick={() => { setRailed((current) => !current); setPeek(false); }}
            title={railed ? 'Keep the menu open' : 'Collapse to icons (the menu opens when you hover it)'}
            type="button"
          >
            {railed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
          </button>
        </div>

        <SideNav groups={visibleGroups} onNavigate={() => { setMenuOpen(false); setPeek(false); }} railed={compact} />

      </aside>
      <main>
        <MobileHeader onOpenMenu={() => setMenuOpen(true)} onSearch={setSearch} search={search}
                      title={navigation.find((item) => item.key === activeKey)?.label ?? 'Vanam'} />
        <OrgAwayBanner />
        <OrgChooserOnLogin />
        <ChangeReasonHost />
        {!isSupabaseConfigured && (
          <p className="offline-banner">
            No Supabase connection is configured, so this is the offline prototype with sample data.
            Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY to work against a database.
          </p>
        )}
        <Routes>
          <Route element={<Navigate replace to={`/${landing}`} />} path="/" />
          {navigation.map((item) => (
            <Route
              element={<RequireAuth navKey={item.key}><item.component /></RequireAuth>}
              key={item.key}
              path={`/${item.key}`}
            />
          ))}
          <Route element={<Navigate replace to={`/${landing}`} />} path="*" />
        </Routes>
      </main>
      <BottomNav />
      <ToastHost />
    </div>
    </GridToolbarProvider>
  );
}
